import React, { useState, useEffect, useCallback } from 'react';
import { marketplaceApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, CreditCard, Home, Repeat, Star, TrendingUp, Shield, 
  CheckCircle, ExternalLink, Loader2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  credit_builder_loan: Building2,
  tradeline: Repeat,
  rent_reporting: Home,
  subscription_reporting: CreditCard
};

const IMPACT_COLORS = {
  'Payment history': 'bg-green-100 text-green-700',
  'Credit mix': 'bg-blue-100 text-blue-700',
  'Utilization': 'bg-purple-100 text-purple-700',
  'Account age': 'bg-amber-100 text-amber-700'
};

const BADGE_STYLES = {
  'Popular': 'bg-orange-100 text-orange-700 border-orange-200',
  'New': 'bg-teal-100 text-teal-700 border-teal-200',
  'No Credit Check': 'bg-green-100 text-green-700 border-green-200',
  'Reports Monthly': 'bg-blue-100 text-blue-700 border-blue-200'
};

export default function CreditMarketplace() {
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(function() {
    async function fetchData() {
      try {
        const [providersRes, categoriesRes] = await Promise.all([
          marketplaceApi.getCreditProviders(),
          marketplaceApi.getCreditCategories()
        ]);
        setProviders(providersRes.providers || []);
        setCategories(categoriesRes.categories || []);
      } catch (err) {
        console.error('Failed to fetch marketplace data:', err);
        toast.error('Failed to load marketplace');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleProviderClick = useCallback(async function(provider) {
    if (!provider.is_active || !provider.cta_url) {
      return;
    }
    
    try {
      await marketplaceApi.trackCreditClick(provider.provider_id);
    } catch (err) {
      console.error('Failed to track click:', err);
    }
    
    let url = provider.cta_url;
    if (provider.affiliate_param) {
      const separator = url.includes('?') ? '&' : '?';
      url = url + separator + provider.affiliate_param;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const filteredProviders = selectedCategory === 'all' 
    ? providers 
    : providers.filter(function(p) { return p.category === selectedCategory; });

  function renderProviderCard(provider) {
    const IconComponent = CATEGORY_ICONS[provider.category] || CreditCard;
    const isActive = provider.is_active && provider.cta_url;
    
    return (
      <Card 
        key={provider.provider_id} 
        className="border-slate-200 hover:shadow-lg transition-shadow"
        data-testid={'provider-card-' + provider.provider_id}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              {provider.logo_url ? (
                <img 
                  src={provider.logo_url} 
                  alt={provider.name}
                  className="w-12 h-12 rounded-lg object-contain bg-slate-50 p-1"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                  <IconComponent className="h-6 w-6 text-slate-500" />
                </div>
              )}
              <div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                  {provider.name}
                </CardTitle>
                <p className="text-sm text-slate-500">{provider.reporting_type}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 justify-end max-w-[120px]">
              {(provider.status_badges || []).slice(0, 2).map(function(badge) {
                return (
                  <Badge 
                    key={badge} 
                    variant="outline" 
                    className={'text-xs ' + (BADGE_STYLES[badge] || 'bg-slate-100 text-slate-700')}
                  >
                    {badge}
                  </Badge>
                );
              })}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600 line-clamp-2">{provider.description}</p>
          
          {/* Bureaus */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Reports to:</span>
            <div className="flex gap-1">
              {(provider.bureaus || []).map(function(bureau) {
                return (
                  <Badge key={bureau} variant="outline" className="text-xs bg-slate-50">
                    {bureau}
                  </Badge>
                );
              })}
            </div>
          </div>
          
          {/* Cost */}
          {(provider.monthly_cost_min || provider.monthly_cost_max) && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Monthly cost:</span>
              <span className="text-sm font-medium text-slate-700">
                {provider.monthly_cost_min === provider.monthly_cost_max || !provider.monthly_cost_max
                  ? '$' + (provider.monthly_cost_min || 0)
                  : '$' + provider.monthly_cost_min + ' - $' + provider.monthly_cost_max
                }
              </span>
            </div>
          )}
          
          {/* Impact Tags */}
          {(provider.impact_tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {provider.impact_tags.map(function(tag) {
                return (
                  <Badge 
                    key={tag} 
                    className={'text-xs ' + (IMPACT_COLORS[tag] || 'bg-slate-100 text-slate-600')}
                  >
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                );
              })}
            </div>
          )}
          
          {/* CTA Button */}
          <Button
            className={isActive ? 'w-full btn-teal' : 'w-full bg-slate-200 text-slate-500 cursor-not-allowed'}
            onClick={function() { handleProviderClick(provider); }}
            disabled={!isActive}
            data-testid={'cta-' + provider.provider_id}
          >
            {isActive ? (
              <>
                {provider.cta_label || 'Learn More'}
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            ) : (
              'Coming Soon'
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="credit-marketplace">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Credit Builder Marketplace
        </h1>
        <p className="text-slate-600 mt-1">
          Discover trusted services to build positive credit history
        </p>
      </div>

      {/* Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="flex-wrap h-auto gap-2 bg-transparent p-0">
          <TabsTrigger 
            value="all" 
            className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700"
          >
            All Services
          </TabsTrigger>
          {categories.map(function(cat) {
            const Icon = CATEGORY_ICONS[cat.id] || CreditCard;
            return (
              <TabsTrigger 
                key={cat.id} 
                value={cat.id}
                className="data-[state=active]:bg-teal-100 data-[state=active]:text-teal-700"
              >
                <Icon className="h-4 w-4 mr-2" />
                {cat.name}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <TabsContent value={selectedCategory} className="mt-6">
          {filteredProviders.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <Building2 className="h-12 w-12 text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No providers yet</h3>
                <p className="text-slate-500 mt-1">
                  Check back soon for credit building options
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map(renderProviderCard)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Compliance Disclosure */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              We may receive compensation from partner links. These are third-party services. 
              We do not provide lending or investment advice. Credit and financial outcomes vary.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
